import { Modal, useModals } from 'almanac-ui/Modal'
import Styles from './CreateBrandStyleModal.module.scss'
import OnboardingStyles from '../../OnboardingBrandVoiceModal/OnboardingBrandVoiceModal.module.scss'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { GoToBlazeHomeButton } from 'components/WelcomeModal/GoToBlazeHomeButton'
import { ChevronLeft, HighDetailedStyle } from 'blaze-ui/icons/24'
import { Heading } from 'blaze-ui/Heading/Heading'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useEffect, useState } from 'react'
import { BrandVisualAnalysisProcessingModal } from 'components/BrandVisualAnalysisProcessingModal/BrandVisualAnalysisProcessingModal'
import { useCreateBrandVisualAnalysis } from 'hooks/queries/brandVisualAnalyses/useCreateBrandVisualAnalysis'
import { SkipButton } from 'components/Onboarding/CreateBrandStyleModal/SkipButton'
import { BrandWizardVisualAnalysisModal } from 'components/BrandWizardVisualAnalysisModal/BrandWizardVisualAnalysisModal'
import { useMutation, useQueryClient } from 'react-query'
import { showIntegrationErrorToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { ExternalIntegrationEnum, ExternalIntegrationsClient } from 'api/clients/ExternalIntegrationsClient'
import { reportError } from 'utils/error'
import { BrandWizardNewDesignModal } from 'components/BrandWizardNewDesignModal/BrandWizardNewDesignModal'
import { ConnectableAccount, IntegrationAuthsClient } from 'api/clients/IntegrationAuthsClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { SelectConnectableAccountsModal } from 'components/SelectConnectableAccountsModal/SelectConnectableAccountsModal'
import { showIntegrationSuccessToast } from 'common/ToastMessage/ShowToastMessage'
import { useCreateExternalIntegration } from 'screens/OAuthRedirectV2/hooks'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useRootStore } from 'stores/RootStore'
import { SelectConnectableAccountsDrawer } from 'components/SelectConnectableAccountsDrawer/SelectConnectableAccountsDrawer'
import { buildErrorMessage } from 'utils'
import { MobileConnectFacebookButton } from 'components/Onboarding/CreateBrandStyleModal/MobileConnectFacebookButton'
import { MobileConnectInstagramButton } from 'components/Onboarding/CreateBrandStyleModal/MobileConnectInstagramButton'
import { DesktopConnectFacebookButton } from 'components/Onboarding/CreateBrandStyleModal/DesktopConnectFacebookButton'
import { DesktopConnectInstagramButton } from 'components/Onboarding/CreateBrandStyleModal/DesktopConnectInstagramButton'
import cx from 'classnames'
import { NoAccountsFoundModal } from 'screens/OAuthRedirectV2/NoAccountsFound/NoAccountsFoundModal'
import { NoAccountsFoundDrawer } from 'screens/OAuthRedirectV2/NoAccountsFound/NoAccountsFoundDrawer'
import { fetchConnectableAccounts } from 'hooks/queries/workspaces/integrationAuths/useConnectableAccounts'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'

export const createIntegrationAuths = async ({
  workspaceId,
  provider,
  params,
}: {
  workspaceId: number
  params: any
  provider: ExternalIntegrationEnum
}) => {
  const response = await IntegrationAuthsClient.create({ workspaceId, provider, params })

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(
      buildErrorMessage(response.data.errors) ||
        'An error occurred! Our team has been notified and we are looking into it.',
    )
  }

  return response
}

export const CreateBrandStyleModal = ({ voiceId }: { voiceId: number }) => {
  const { mutateAsync: createBrandVisualAnalysis } = useCreateBrandVisualAnalysis()
  const workspaceId = useCurrentWorkspaceId()
  const currentWorkspace = useCurrentWorkspace()
  const { data, isLoading, refetch } = useExternalIntegrations()
  const { mutateAsync: disconnect } = useMutation(ExternalIntegrationsClient.deleteExternalIntegration)
  const queryClient = useQueryClient()
  const { openModal, closeModal, closeStack } = useModals()
  const [isConnecting, setIsConnecting] = useState(false)
  const [popupWindow, setPopupWindow] = useState<Window | null>(null)
  const { mutateAsync: createIntegrationAuth } = useMutation(createIntegrationAuths)
  const { mutateAsync: createExternalIntegration } = useCreateExternalIntegration()
  const { isMobile } = useResponsiveDesign()
  const { drawersStore } = useRootStore()
  const { openDrawer, closeDrawer } = drawersStore

  const handleSubmit = (integrationAuthId: number) => async (accounts: ConnectableAccount[]) => {
    const results = await Promise.all(
      accounts.map((account) =>
        createExternalIntegration({
          workspaceId,
          params: { ...account, integration_auth_id: integrationAuthId },
        }),
      ),
    )

    await refetch()

    results.forEach((res) => {
      if (res.type === SUCCESS_RESPONSE_TYPE) {
        showIntegrationSuccessToast(`Successfully connected to ${res.data.external_integration.name}!`)
      } else {
        showIntegrationErrorToast('Failed to connect account')
      }
    })

    if (results.every((r) => r.type === SUCCESS_RESPONSE_TYPE)) {
      try {
        const { external_integration } = results[0].data

        const { id } = await createBrandVisualAnalysis({ external_integration_id: external_integration.id })
        openModal(
          BrandVisualAnalysisProcessingModal,
          {
            integration: external_integration,
            id,
            onConfirm: (kit) => {
              openModal(BrandWizardNewDesignModal, { brandVisualKit: kit }, { underlayVariant: 'brandStyle' })
            },
            onSuccess: (props) => {
              closeModal()
              openModal(BrandWizardVisualAnalysisModal, props, { underlayVariant: 'brandStyle' })
            },
          },
          { underlayVariant: 'brandStyle' },
        )
      } catch (e) {
        showIntegrationErrorToast('There was an issue connecting to the account. Please try again.')
        reportError(e)
      } finally {
        setIsConnecting(false)
      }
    }
  }

  useEffect(() => {
    const listener = async (event: MessageEvent) => {
      // eslint-disable-next-line no-restricted-properties
      if (window.location.origin !== event.origin) return

      if (event.data.params && event.data.provider) {
        const { params, provider } = event.data
        popupWindow?.close()

        if (event.data.params.error) {
          showIntegrationErrorToast('There was an error connecting the account. Please try again.')
          setIsConnecting(false)
          return
        }

        try {
          const { data: integrationAuth } = await createIntegrationAuth({
            provider,
            params,
            workspaceId,
          })
          const { data: connectableAccounts } = await queryClient.fetchQuery(
            ['workspaces', workspaceId, 'integrationAuths', integrationAuth.id, 'connectableAccounts'],
            () => fetchConnectableAccounts(integrationAuth.id, workspaceId),
          )

          if (connectableAccounts.length === 0) {
            isMobile
              ? openDrawer(NoAccountsFoundDrawer, { provider, onClose: closeDrawer })
              : openModal(NoAccountsFoundModal, { provider, onClose: closeModal })
            return
          }

          const props = {
            connectableAccounts,
            provider,
            onCancel: () => {},
            handleSubmit: handleSubmit(integrationAuth.id),
            isSingleSelect: currentWorkspace?.is_virtual_marketer,
          }

          isMobile
            ? openDrawer(SelectConnectableAccountsDrawer, props)
            : openModal(
                SelectConnectableAccountsModal,
                {
                  ...props,
                  onCancel: () => {
                    closeStack()
                  },
                },
                { newStack: true },
              )
        } catch (e) {
          showIntegrationErrorToast('There was an error connecting the account. Please try again.')
          reportError(e)
        } finally {
          setIsConnecting(false)
        }
      }

      if (event.data.error) {
        showIntegrationErrorToast(event.data.error)
        setIsConnecting(false)
      }
    }

    window.addEventListener('message', listener)

    return () => window.removeEventListener('message', listener)
  }, [isMobile, popupWindow, queryClient, createIntegrationAuth, handleSubmit, workspaceId]) // eslint-disable-line react-hooks/exhaustive-deps

  // check for when the popup window is closed
  useEffect(() => {
    if (!popupWindow) return

    const timer = setInterval(function () {
      if (popupWindow.closed) {
        clearInterval(timer)
        setIsConnecting(false)
      }
    }, 1000)

    return () => {
      clearInterval(timer)
    }
  }, [popupWindow])

  const connectButtonProps = {
    onPopupOpen: (popup: Window) => {
      setIsConnecting(true)
      setPopupWindow(popup)
    },
    isDisabled: isConnecting || isLoading,
    onPress: async () => {
      // disconnect any existing accounts to prevent getting in a stuck state
      const promises = (data?.external_integrations || []).map(({ id }) => disconnect({ workspaceId, id }))
      await Promise.allSettled(promises)
    },
  }

  return (
    <Modal.Root onPressOutside={() => {}} bodyClassName={OnboardingStyles.modalBody}>
      <div className={cx(OnboardingStyles.wrapper, Styles.wrapper)}>
        <ResponsiveDesign.Mobile>
          <div className={OnboardingStyles.top}>
            <IconButton icon={() => <ChevronLeft size={20} />} variant="tertiary" size="md" onPress={closeModal} />
            <GoToBlazeHomeButton />
          </div>
        </ResponsiveDesign.Mobile>
        <div className={OnboardingStyles.content}>
          <div className={OnboardingStyles.contentTop}>
            <HighDetailedStyle size={50} />
            <h1 className={OnboardingStyles.heading}>Now, let’s create your Brand Style</h1>
            <Heading level={4}>
              Connect your social account, and we will create Brand Styles for you based on your content!
            </Heading>
            <ResponsiveDesign.Mobile>
              <div className={Styles.connectButtons}>
                <MobileConnectFacebookButton {...connectButtonProps} />
                <MobileConnectInstagramButton {...connectButtonProps} />
              </div>
            </ResponsiveDesign.Mobile>
          </div>
        </div>
        <div className={Styles.connectButtons}>
          <SkipButton voiceId={voiceId} />
          <ResponsiveDesign.Desktop>
            <DesktopConnectFacebookButton {...connectButtonProps} />
            <DesktopConnectInstagramButton {...connectButtonProps} />
          </ResponsiveDesign.Desktop>
        </div>
      </div>
    </Modal.Root>
  )
}
